import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { first } from 'rxjs/operators';
import { environment } from '@env/environment';
import { Config } from './config.model';
import { PipelineData, PipelinesData } from '@app/models/pipeline_data';
import { updateProtectedResourceMap } from '@app/services/authentication/protected-resource-map';

@Injectable({
	providedIn: 'root'
})
export class AppConfig {
	private readonly CONFIG_PATH = 'assets/config/json/config.json';
	private readonly DEV_CONFIG_PATH = 'assets/config/json/config.dev.json';
	private readonly PIPELINE_DATA_PATH = 'pipeline_data.json';

	private _config: Config | undefined;
	private _pipelineData: PipelinesData | undefined;

	private emptyPipelineData: PipelinesData = {
		process: {
			pipeline_id: '0',
			pipeline_timestamp: new Date(0)
		},
		backend: {
			pipeline_id: '0',
			pipeline_timestamp: new Date(0)
		},
		frontend: {
			pipeline_id: '0',
			pipeline_timestamp: new Date(0)
		}
	};

	constructor(private http: HttpClient) {}

	get instance(): Config {
		return this._config;
	}

	get pipelineData(): PipelinesData {
		return this._pipelineData;
	}

	static instance: Config;

	get(key: string): any {
		return _.get(this._config, key);
	}

	set(key: string, value: any): void {
		return _.set(this._config, key, value);
	}

	public load() {
		return new Promise<void>((resolve, reject) => {
			this.http
				.get(this.DEV_CONFIG_PATH)
				.pipe(first())
				.subscribe({
					next: (config: any) => this.setConfigFile(config, resolve),
					error: () => {
						this.http
							.get(this.CONFIG_PATH)
							.pipe(first())
							.subscribe({
								next: (config: Config) => this.setConfigFile(config, resolve),
								error: (response: any) => {
									reject(`Could not load file '${this.CONFIG_PATH}': ${JSON.stringify(response)}`);
								}
							});
					}
				});
		});
	}

	public loadPipelineData() {
		return new Promise<PipelinesData>((resolve, reject) => {
			if (!this._config) {
				reject(`Could not load pipeline data from backend: config file not loaded}`);
			}
			this.http
				.get(this.PIPELINE_DATA_PATH)
				.pipe(first())
				.subscribe({
					next: (frontendData: PipelineData) => {
						// `MsalInterceptor` attaches the Bearer token automatically for this URL
						// (matches `protectedResourceMap`'s `<backendUrl>/ui/*` entry) — no manual
						// header injection needed anymore (was done via the now-removed JwtInterceptor).
						this.http
							.get(this._config.backendUrl + '/ui/pipeline-data')
							.pipe(first())
							.subscribe({
								next: (data: PipelinesData) => {
									data.frontend = frontendData;
									this._pipelineData = data;
									resolve(data);
								},
								error: (response: any) => {
									console.warn('Could not load pipeline data from backend', response);
									this._pipelineData = this.emptyPipelineData;
									resolve(this.emptyPipelineData);
								}
							});
					},
					error: (response) => {
						console.warn('Could not load pipeline data from backend', response);
						this._pipelineData = this.emptyPipelineData;
						resolve(this.emptyPipelineData);
					}
				});
		});
	}

	private setConfigFile(config: Config, resolve) {
		if (environment.useProxy && config.backendUrlWithProxy) {
			config.backendUrl = config.backendUrlWithProxy;
			console.info('Using proxy for backend url: ' + config.backendUrl);
		}
		this._config = config;
		AppConfig.instance = config;
		// See auth.service.ts `protectedResourceMap` comment: this is the moment the real
		// backendUrl becomes known — `MsalInterceptor` was constructed earlier (during this very
		// config-loading HTTP call) with an empty map; this mutates that SAME Map in place so it
		// starts attaching Bearer tokens on every subsequent request, without needing the
		// MSAL_INTERCEPTOR_CONFIG DI provider to be re-created.
		updateProtectedResourceMap(config.backendUrl);
		resolve();
	}
}
